/**
 * We are defining access control in terms of these three parameters
 * hide - Element will not be visible at all.
 * disable - Element will be visible but user will not be able to click it.
 * show - Element will be visible and working properly.
 *
 * Overriding Logic - These permissions will get override in some conditions. Like if a user has two different roles
 * which might give him different permissions on the same element. In this case the higher permission will take
 * precedence over lower one.
 * Eg. in case of hide and disable - disable will be picked
 * and in case of hide and show - show will be picked
 */
export const DISPLAY_MAP = {
  hide: -1,
  disable: 0,
  show: 1,
}

// Elements ids map. Eveyone who wants to add access control to some new elements apart from below once
// have to mention new ids here.

export const ROLES_MAP = {
  admin: {
    key: 'admin',
    roleId: '4543881f-8b0f-41bb-9cd1-0e2deb3d0b16',
    title: 'Admin',
  },
  lineman: {
    key: 'lineman',
    roleId: '4543881f-8b0f-41bb-9cd1-0e2deb3d0b17',
    title: 'Lineman',
  },
  externalFieldWorker: {
    key: 'externalFieldWorker',
    roleId: '4543881f-8b0f-41bb-9cd1-0e2deb3d0b16',
    title: 'External Field Worker',
  },
  fieldOperationsManager: {
    key: 'fieldOperationsManager',
    roleId: '4543881f-8b0f-41bb-9cd1-0e2deb3d0b16',
    title: 'Field Operations Manager',
  },
  FI: {
    key: 'FI',
    roleId: 'ec6faede-9b39-4040-8a3b-357e0ae08f5b',
    title: 'FI',
  },
  FM: {
    key: 'FM',
    roleId: 'ec6faede-9b39-4040-8a3b-357e0ae08f5c',
    title: 'FM',
  },
}

const SITE_PAGE_IDS_MAP = {
  siteListTabs1: 'siteListTabs1',
  siteListTabs2: 'siteListTabs2',
}

const HOME_PAGE_IDS_MAP = {
  homePageMapPopUpDetailsCost: 'homePageMapPopUpDetailsCost',
  homePageFilter1CircuitTrimDropDownOption: 'homePageFilter1CircuitTrimDropDownOption',
  homePageFilter1SegmentTrimDropDownOption: 'homePageFilter1SegmentTrimDropDownOption',
  homePageFilter1SpanTrimDropDownOption: 'homePageFilter1SpanTrimDropDownOption',
  homePageFilter1TreeDropDownOption: 'homePageFilter1TreeDropDownOption',
  homePageFilter0TlDropdownOption: 'homePageFilter0TlDropdownOption',
}

const VIEW_TASK_REPORT_IDS_MAP = {
  viewTaskReport: 'viewTaskReport',
}

const INGEST_TASK_IDS_MAP = {
  ingestTask: 'ingestTask',
  createBNGPlan: 'createBNGPlan',
  exportShapefile: 'exportShapefile',
  dsAutomation: 'dsAutomation',
  bulkCreation: 'bulkCreation',
}

const CYCLE_TRIM_ANALYSIS_LIST_IDS_MAP = {
  analyseHomeExportAnalysis: 'analyseHomeExportAnalysis',
}

const CYCLE_TRIM_ANALYSIS_DETAILS_IDS_MAP = {
  hazardTreeDetailsCostSection: 'hazardTreeDetailsCostSection',
}

const HAZARD_TREE_LIST_IDS_MAP = {
  hazardTreeListDollarACI: 'hazardTreeListDollarACI',
  hazardTreeListDollarTotalCost: 'hazardTreeListDollarTotalCost',
}

const HAZARD_TREE_DETAILS_IDS_MAP = {
  hazardTreeDetailsCostSection: 'hazardTreeDetailsCostSection',
  hazardTreeDetailsActionBtnFooter: 'hazardTreeDetailsActionBtnFooter',
  hazardTreeDetailsEditTreeBtn: 'hazardTreeDetailsEditTreeBtn',
}

const ANALYSIS_LIST_IDS_MAP = {
  analysisListTotalCost: 'analysisListTotalCost',
}

const ANALYSIS_DETAILS_IDS_MAP = {
  analysisDetailTotalCost: 'analysisDetailTotalCost',
  analysisDetailCostPerLM: 'analysisDetailCostPerLM',
  analysisDetailEstCI: 'analysisDetailEstCI',
  analysisDetailCostPerAci: 'analysisDetailCostPerAci',
  analysisDetailReliabilityMetrics: 'analysisDetailReliabilityMetrics',
}

const PLAN_LIST_IDS_MAP = {
  planListTotalCost: 'planListTotalCost',
  planListEstCI: 'planListEstCI',
}

const PLAN_DETAIL_IDS_MAP = {
  planDetailTotalCost: 'planDetailTotalCost',
  planDetailEstCI: 'planDetailEstCI',
  planDetailAvgOutage: 'planDetailAvgOutage',
}

const CREATE_PLAN_IDS_MAP = {
  createPlanTotalCost: 'createPlanTotalCost',
  createPlanEstCI: 'createPlanEstCI',
  createPlanAvgOutage: 'createPlanAvgOutage',
}

//execute is same as trim task
const TRIM_TASK_LIST_IDS_MAP = {
  trimTaskListTotalCost: 'trimTaskListTotalCost',
}

const TRIM_TASK_DETAIL_IDS_MAP = {
  trimTaskDetailTotalCost: 'trimTaskDetailTotalCost',
  trimTaskDetailCostPerLM: 'trimTaskDetailCostPerLM',
  trimTaskDetailReliabilityMetrics: 'trimTaskDetailReliabilityMetrics',
  trimTaskDetailSegmentSpanDrawer: 'trimTaskDetailSegmentSpanDrawer',
}

//execute is same as trim task, tl => transmission line
const TL_TRIM_TASK_LIST_IDS_MAP = {
  tlTrimTaskListTotalCost: 'tlTrimTaskListTotalCost',
}

const TL_TASK_DETAIL_IDS_MAP = {
  tlTrimTaskDetailTotalCost: 'tlTrimTaskDetailTotalCost',
  tlTrimTaskDetailCostPerLM: 'tlTrimTaskDetailCostPerLM',
  tlTrimTaskDetailAvgOutage: 'tlTrimTaskDetailAvgOutage',
}

const UPDATE_PROGRESS_IDS_MAP = {
  updateProgressFeature: 'updateProgressFeature',
}

const RM_EXECUTE_DETAIL_IDS_MAP = {
  segmentAnalysisLink: 'segmentAnalysisLink',
  wuiTierAndCtPlan: 'wuiTierAndCtPlan',
}

const STATUS_FLOW = {
  htStatusFlow: 'htStatusFlow',
  ctStatusFlow: 'ctStatusFlow',
}

export const ROLE_CONSTANTS_IDS_MAP = {
  ...STATUS_FLOW,
}

export const NC_DOT_FEATURES = {
  routeUpdatePopup: 'routeUpdatePopup',
  adminUser: 'adminUser',
  cmePlan: 'cmePlan',
  dmePlan: 'dmePlan',
  centralPlan: 'centralPlan',
  // plan county dropdown card: used to change active county
  cmeSummaryCard: 'cmeSummaryCard',
  commomSummaryCard: 'commomSummaryCard',
  understandModuleJobList: 'understandModuleJobList',
  planModuleUpdateFeatures: 'planModuleUpdateFeatures',
  planModuleSubmitPlanButton: 'planModuleSubmitPlanButton',
  understandModuleCreateJobButton: 'understandModuleCreateJobButton',
  understandModuleExport: 'understandModuleExport',
  planModuleExport: 'planModuleExport',
}

export const MODULE_IDS_MAP = {
  aoiModule: 'AOIModule',
  siteModule: 'siteModule',
}

export const FEATURE_IDS_MAP = {
  ...HOME_PAGE_IDS_MAP,
  ...CYCLE_TRIM_ANALYSIS_LIST_IDS_MAP,
  ...CYCLE_TRIM_ANALYSIS_DETAILS_IDS_MAP,
  ...HAZARD_TREE_LIST_IDS_MAP,
  ...HAZARD_TREE_DETAILS_IDS_MAP,
  ...ANALYSIS_LIST_IDS_MAP,
  ...ANALYSIS_DETAILS_IDS_MAP,
  ...PLAN_LIST_IDS_MAP,
  ...PLAN_DETAIL_IDS_MAP,
  ...CREATE_PLAN_IDS_MAP,
  ...TRIM_TASK_LIST_IDS_MAP,
  ...TRIM_TASK_DETAIL_IDS_MAP,
  ...TL_TRIM_TASK_LIST_IDS_MAP,
  ...TL_TASK_DETAIL_IDS_MAP,
  ...UPDATE_PROGRESS_IDS_MAP,
  ...RM_EXECUTE_DETAIL_IDS_MAP,
  ...NC_DOT_FEATURES,
  ...SITE_PAGE_IDS_MAP,
  ...VIEW_TASK_REPORT_IDS_MAP,
  ...INGEST_TASK_IDS_MAP,
}
